import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';
import {useApiMutation} from '@legit.health/backend-client';
import {
  generatePath,
  AnonymousDiagnosticReport,
  BodySite,
  DiagnosticReportId,
  PathologyCode,
  PathologyId,
  ScoringSystem,
  ScoringSystemId
} from '@legit.health/ui';
import {Box} from '@mui/material';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Backdrop from '@/components/Backdrop';
import {useParamsContext} from '@/contexts/ParamsContext';
import useLocale from '@/hooks/useLocale';
import QuestionnairesFormView from './QuestionnairesFormView';
import {FormValues} from './interfaces/FormValues';
import createDefaultValues from './utils/createDefaultValues';
import createSchema from './utils/createSchema';
import processScoringSystems from './utils/processScoringSystems';

export default function QuestionnairesForm({
  bodySite,
  diagnosisSupportId,
  onReceiveDiagnosticReportResult,
  pathology,
  scoringSystemsById
}: {
  bodySite: BodySite;
  diagnosisSupportId: DiagnosticReportId;
  onReceiveDiagnosticReportResult: (diagnosticReport: AnonymousDiagnosticReport) => void;
  pathology: {
    id: PathologyId;
    code: PathologyCode;
    name: string;
    scoringSystemsIds: number[];
  };
  scoringSystemsById: Record<ScoringSystemId, ScoringSystem>;
}) {
  const locale = useLocale();
  const {t} = useTranslation(['app', 'ui']);
  const {companyCallbackUrl, isForPatient, showQuestionnairesHeader} = useParamsContext();

  const isSending = useRef(false);
  const scoringSystems = pathology.scoringSystemsIds
    .map((ssId) => scoringSystemsById[ssId])
    .filter((ss) => ss.hasFacetsToBeAskedInQuestionnaire);

  const form = useForm<FormValues>({
    resolver: yupResolver(createSchema({questionnaires: scoringSystems})) as any,
    defaultValues: createDefaultValues({
      scoringSystems,
      diagnosisSupportId,
      companyCallbackUrl
    })
  });
  const {
    mutate,
    isPending,
    isSuccess,
    error: apiError
  } = useApiMutation<AnonymousDiagnosticReport>({
    options: {
      onSuccess: (response) => onReceiveDiagnosticReportResult(response.data)
    }
  });

  const handleSubmit = useCallback(
    function (values: FormValues) {
      mutate({
        path: generatePath(
          '/diagnosis-support/:diagnosticReportId',
          {diagnosticReportId: values.diagnosticReportId},
          {
            _locale: locale
          }
        ),
        method: 'PATCH',
        body: {
          scoringSystems: processScoringSystems(values.scoringSystems),
          companyCallbackUrl: values.companyCallbackUrl
        }
      });
    },
    [mutate, locale]
  );

  const {getValues} = form;

  useEffect(
    function () {
      if (isSending.current) {
        return;
      }
      if (scoringSystems.length === 0) {
        isSending.current = true;
        handleSubmit(getValues());
      }
    },
    [handleSubmit, getValues, scoringSystems.length]
  );
  useLayoutEffect(function () {
    window.scrollTo(0, 0);
  }, []);

  if (scoringSystems.length === 0) {
    return (
      <Box sx={{bgcolor: '#fff', minHeight: '100vh'}}>
        <Backdrop />
      </Box>
    );
  }

  return (
    <QuestionnairesFormView
      apiError={apiError}
      isPending={isPending}
      isSuccess={isSuccess}
      bodySite={bodySite}
      isForPatient={isForPatient}
      form={form}
      onSubmit={handleSubmit}
      scoringSystems={scoringSystems}
      showQuestionnairesHeader={showQuestionnairesHeader}
      t={t}
    />
  );
}
